/**
 * La búsqueda de postulaciones, con un solo texto.
 *
 * Antes eran dos desplegables —tipo de participante y estado— más un botón de
 * buscar y otro de limpiar. Servían de poco: de cien propuestas, filtrar por
 * "persona natural" deja ochenta, y lo que uno suele recordar es el nombre
 * del proyecto o su línea, que no se podían buscar.
 *
 * El texto busca a la vez en el nombre del proyecto, el radicado, la línea,
 * la categoría y el estado, que es lo que la tabla muestra.
 */

/** Sin tildes, sin mayúsculas y sin espacios de más. */
const normalizar = (valor: any): string =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * "Sin revisar" se muestra como "Inscrito sin revisar", así que se busca por
 * lo que la persona lee en la tabla y no por lo que hay guardado.
 */
const estadoComoSeLee = (postulacion: any): string =>
  String(postulacion?.estado) === 'Sin revisar'
    ? 'Inscrito sin revisar'
    : String(postulacion?.estado ?? '');

/**
 * ¿Esta postulación responde a lo que se escribió?
 *
 * Todas las palabras tienen que aparecer en alguna parte, no necesariamente
 * en la misma: así "danza aceptada" encuentra la de danza que ya lo está,
 * aunque el nombre y el estado vayan por separado.
 */
export const coincideLaPostulacion = (postulacion: any, texto: string): boolean => {
  const buscado = normalizar(texto);
  if (buscado === '') return true;

  const dondeBuscar = normalizar(
    [
      postulacion?.nombre_propuesta,
      postulacion?.radicado,
      postulacion?.linea_convocatoria,
      postulacion?.categoria_linea_convocatoria,
      estadoComoSeLee(postulacion),
    ].join(' ')
  );

  return buscado.split(' ').every((palabra) => dondeBuscar.indexOf(palabra) > -1);
};

/** Las que responden, en el mismo orden en que venían. */
export const filtrarPostulaciones = (postulaciones: any, texto: string): any[] =>
  (Array.isArray(postulaciones) ? postulaciones : []).filter((p: any) =>
    coincideLaPostulacion(p, texto)
  );
